import styled from '@emotion/styled';
import type { TrackerPrototype } from '@racemap/sdk/schema/trackers';
import { Button, Divider, Flex, Modal } from 'antd';
import { type FC, useState } from 'react';
import { TagSelect } from 'src/components/BasicComponents/TagSelect/TagSelect';
import { useStore } from 'src/store/reducers';
import type { ManagedTracker } from 'src/store/trackers/trackers_reducers';
import { TrackersTable } from './TrackersTable';

interface Props {
  isAddTrackersModalOpen: boolean;
  trackersToAdd: Array<TrackerPrototype> | null;
  setTrackersToAdd: (trackersToAdd: Array<TrackerPrototype>) => void;
  setIsAddTrackersModalOpen: (value: boolean) => void;
  addTrackers: (
    trackersToAdd: Array<TrackerPrototype>,
  ) => Promise<Array<ManagedTracker> | undefined>;
}

export const AddTrackersModal: FC<Props> = ({
  trackersToAdd,
  setTrackersToAdd,
  setIsAddTrackersModalOpen,
  isAddTrackersModalOpen,
  addTrackers,
}) => {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const { updateOrCreateTrackerTag } = useStore((s) => s.trackers.actions);

  const handleAddTrackers = async () => {
    if (!trackersToAdd || trackersToAdd.length === 0) {
      setIsAddTrackersModalOpen(false);
      return;
    }

    try {
      const trackers = await addTrackers(trackersToAdd);
      await updateOrCreateTrackerTag(selectedTag, trackers);
    } catch (error) {
      console.error('Error adding trackers:', error);
    } finally {
      setIsAddTrackersModalOpen(false);
    }
  };

  return (
    <Modal
      title={`Add ${trackersToAdd?.length} Trackers`}
      open={isAddTrackersModalOpen}
      width={700}
      onCancel={() => setIsAddTrackersModalOpen(false)}
      onClose={() => setSelectedTag(null)}
      footer={
        <Button onClick={handleAddTrackers} type="primary" disabled={!trackersToAdd?.length}>
          Add Trackers
        </Button>
      }
    >
      {
        <>
          <TrackersTable trackersToAdd={trackersToAdd} setTrackersToAdd={setTrackersToAdd} />
          <StyledDivider />
          <Container>
            <Label>Assign Trackers to a Tag</Label>
            <SelectTag setSelectedTag={setSelectedTag} />
          </Container>
        </>
      }
    </Modal>
  );
};

const SelectTag = styled(TagSelect)`
  background-color: rebeccapurple;
`;

const StyledDivider = styled(Divider)`
  margin: 20px 0 10px 0;
`;

const Container = styled(Flex)`
  justify-content: flex-start;
  flex-direction: column;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: medium;
  margin-bottom: 10px;
`;
